
import { colorToRgb, extractColorFromElement, RgbColor } from "./colorUtils";
import { ScrollableItems } from "./ScrollableItems";

const selectors = {
  tab: "[role='tab']",
  tabContainer: ".tab-item",
  panel: "[role='tabpanel']",
  tabIcon: ".js-tab-icon",
  iconWrapper: ".icon-wrapper",
  tabItemsWrapper: ".tab-items-wrapper"
}

const classes = {
  active: "active-tab",
  hover: "hover",
  backgroundIcon: "background-icon",
}

const attributes = {
  selected: "aria-selected",
  tabIndex: "tabindex"
}

const transparentImageData =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAC0lEQVQYV2NgAAIAAAUAAarVyFEAAAAASUVORK5CYII=";

class Tabs {
  private el: HTMLElement;
  private tabs: HTMLElement[];
  private panels: HTMLElement[];
  private tabIcons: HTMLImageElement[];
  private rgbDefault: RgbColor;
  private rgbActive: RgbColor;
  private rgbHover: RgbColor;
  private tabContainers: HTMLLIElement[];
  private scrollableItems: ScrollableItems;
  private tabItemsWrapper: HTMLElement;

  constructor(el: HTMLElement) {
    this.el = el;
    this.tabs = Array.from(el.querySelectorAll(selectors.tab));
    this.panels = Array.from(el.querySelectorAll(selectors.panel));
    this.tabIcons = Array.from(el.querySelectorAll<HTMLImageElement>(selectors.tabIcon));
    this.tabContainers = Array.from(el.querySelectorAll<HTMLLIElement>(selectors.tabContainer));
    this.tabItemsWrapper = el.querySelector(selectors.tabItemsWrapper);
  }

  init() {
    this.scrollableItems = new ScrollableItems(
      this.el,
      this.tabItemsWrapper,
      this.tabContainers,
    );
    this.scrollableItems.attachEvents();

    this.tabs.forEach((tab, index) => {
      tab.addEventListener("click", (e) => this.activate(e, index));
      tab.addEventListener("keydown", (e) => this.handleKeydown(e, index));
    });

    this.initIcons();

    return this;
  }

  private activate(e: MouseEvent | KeyboardEvent, index: number) {
    e.preventDefault();

    this.tabs.forEach((tab, i) => {
      const isActive = i === index;
      const tabContainer = tab.closest("li");

      tabContainer.classList.toggle(classes.active, isActive);
      tab.setAttribute(attributes.selected, String(isActive));
      tab.setAttribute(attributes.tabIndex, isActive ? "0" : "-1");
    });

    this.panels.forEach((panel, i) => {
      panel.classList.toggle(classes.active, i === index);
    });

    const activeTab = this.tabs[index];
    const id = activeTab.getAttribute("href")?.replace("#", "");

    // Native CustomEvent dual-publish for ES6 consumers (AB-8816)
    document.dispatchEvent(
      new CustomEvent("tabsChanged", {
        detail: { id, selector: ".js-comp-tab" },
      }),
    );
    document.dispatchEvent(
      new CustomEvent("tabItemShown", { detail: { id } }),
    );
  }

  private handleKeydown(e: KeyboardEvent, currentIndex: number) {
    if (e.code === "Space" || e.code === "Enter") {
      this.activate(e, currentIndex);
    } else if (e.code === "ArrowRight") {
      e.preventDefault();
      const rightElement = (currentIndex + 1) % this.tabs.length;
      this.tabs[rightElement].focus();
    } else if (e.code === "ArrowLeft") {
      e.preventDefault();
      const leftElement = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
      this.tabs[leftElement].focus();
    } else if (e.code === "Home") {
      e.preventDefault();
      this.tabs[0].focus();
    } else if (e.code === "End") {
      e.preventDefault();
      this.tabs[this.tabs.length - 1].focus();
    }
  }

  private initIcons = () => {
    const firstTab = this.tabs[0];
    const isFirstTabActive = firstTab.classList.contains(classes.active);

    firstTab.classList.remove(classes.active);
    const iconColorDefault = extractColorFromElement(firstTab);
    firstTab.classList.add(classes.active);
    const iconColorActive = extractColorFromElement(firstTab);
    firstTab.classList.add(classes.hover);
    const iconColorHover = extractColorFromElement(firstTab);

    this.rgbDefault = colorToRgb(iconColorDefault);
    this.rgbActive = colorToRgb(iconColorActive);
    this.rgbHover = colorToRgb(iconColorHover);

    firstTab.classList.toggle(classes.active, isFirstTabActive);
    firstTab.classList.remove(classes.hover);

    this.tabIcons.forEach(icon => this.renderIcon(icon));
  }

  private renderIcon = (img: HTMLImageElement) => {
    let originalPngUrl = img.getAttribute("src");

    if (
      originalPngUrl &&
      originalPngUrl.endsWith(".svg") &&
      navigator.userAgent.indexOf("Firefox") !== -1
    ) {
      //only for firefox
      originalPngUrl =
        "/bin/straumann/svgmodifier?svgPath=" + img.getAttribute("src");
    }

    const bgCanvas = document.createElement("canvas");
    bgCanvas.width = 160;
    bgCanvas.height = 480;

    const iconCanvas = document.createElement("canvas");
    iconCanvas.width = 160;
    iconCanvas.height = 160;

    const tempImg = new Image();

    tempImg.onload = () => {
      this.onIconLoad(bgCanvas, iconCanvas, tempImg, img);
    };

    tempImg.src = originalPngUrl;
  }

  private onIconLoad = (
    bgCanvas: HTMLCanvasElement,
    iconCanvas: HTMLCanvasElement,
    tempImg: HTMLImageElement,
    img: HTMLImageElement,
  ) => {
    const bgCtx = bgCanvas.getContext("2d");
    const iconCtx = iconCanvas.getContext("2d");
    
    if (!bgCtx || !iconCtx) {
      return;
    }

    iconCtx.drawImage(tempImg, 0, 0, 160, 160);

    const original = iconCtx.getImageData(
      0,
      0,
      iconCanvas.width,
      iconCanvas.height,
    );

    const stateDefault = original;
    const stateActive = original;
    const stateHover = original;

    this.colorizeSingleIcon(stateDefault, this.rgbDefault, bgCtx, 0);
    this.colorizeSingleIcon(stateActive, this.rgbActive, bgCtx, 160);
    this.colorizeSingleIcon(stateHover, this.rgbHover, bgCtx, 320);

    const transformedData = bgCanvas.toDataURL();

    img.setAttribute("src", transparentImageData);
    img.style.backgroundImage = `url(${transformedData})`;
    img.classList.add(classes.backgroundIcon);
    img.closest<HTMLElement>(selectors.iconWrapper).style.opacity = "1";
  }

  private colorizeSingleIcon = (icon: ImageData, color: RgbColor, ctx: CanvasRenderingContext2D, offset: number) => {
    for (let i = 0, length = icon.data.length; i < length; i += 4) {
      icon.data[i] = color.r;
      icon.data[i + 1] = color.g;
      icon.data[i + 2] = color.b;
    }
    ctx.putImageData(icon, 0, offset);
  }
}

const components = {
  "tabs": Tabs
};

export function initComponents(root = document) {
  Object.entries(components).forEach(([name, Component]) => {
    root.querySelectorAll<HTMLElement>(`[data-component="${name}"]`).forEach(el => {
      new Component(el).init();
    });
  });
}

/**
 * Capture the pristine entry hash before any component can mutate it.
 * If it contains a tab deep-link (`#tab=…` or `&tab=…`), wait until the
 * page has fully loaded (tabs are initialised by then), restore the full
 * hash if an init-time click shortened it, then re-fire `hashchange` so
 * article-list components can activate the correct tab + tag filter.
 *
 * Called automatically at module-load time. Exported for unit testing.
 */
export function restoreDeepLink(): void {
  const deepLinkHash = window.location.hash;
  if (!/[#&]tab=/.test(deepLinkHash)) return;

  window.addEventListener(
    "load",
    () => {
      if (window.location.hash !== deepLinkHash) {
        history.replaceState(null, "", deepLinkHash);
      }
      window.dispatchEvent(new HashChangeEvent("hashchange"));
    },
    { once: true },
  );
}

// Capture the entry hash before any component (including initComponents below)
// can mutate it.  Must run at module-load time, before DOMContentLoaded fires.
restoreDeepLink();

if (document.readyState !== "loading") {
  initComponents();
} else {
  document.addEventListener("DOMContentLoaded", () => initComponents());
}
